import { useMemo, useState } from 'react'
import type { ClubEvent } from '../../types'
import { eventMeta, parseLocalDate } from './eventMeta'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Public-facing combined calendar: upcoming events as a date-chipped list. */
export function EventsTimeline({ events }: { events: ClubEvent[] }) {
  const [showPast, setShowPast] = useState(false)

  const { upcoming, past } = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const endOf = (e: ClubEvent) => parseLocalDate(e.endDate || e.date)
    const up: ClubEvent[] = []
    const pa: ClubEvent[] = []
    for (const e of events) {
      if (endOf(e) >= today) up.push(e)
      else pa.push(e)
    }
    // upcoming already ascending from the API; past shown most-recent first
    pa.reverse()
    return { upcoming: up, past: pa }
  }, [events])

  if (events.length === 0) return null

  const list = showPast ? past : upcoming

  return (
    <section className="border border-border rounded-lg p-6 bg-surface">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-semibold text-text">Events &amp; Announcements</h2>
        {past.length > 0 && (
          <button
            type="button"
            onClick={() => setShowPast(!showPast)}
            className="text-sm text-primary hover:underline"
          >
            {showPast ? 'Show upcoming' : 'Show past'}
          </button>
        )}
      </div>

      {list.length === 0 ? (
        <p className="text-sm text-text-muted">
          {showPast ? 'No past events.' : 'No upcoming events scheduled.'}
        </p>
      ) : (
        <ol className="space-y-4">
          {list.map((e, i) => {
            const d = parseLocalDate(e.date)
            const prev = i > 0 ? parseLocalDate(list[i - 1].date) : null
            const showMonth =
              !prev || prev.getMonth() !== d.getMonth() || prev.getFullYear() !== d.getFullYear()
            return (
              <li key={e.id}>
                {showMonth && (
                  <p className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-2">
                    {MONTHS[d.getMonth()]} {d.getFullYear()}
                  </p>
                )}
                <EventRow event={e} />
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}

function EventRow({ event }: { event: ClubEvent }) {
  const meta = eventMeta(event.type)
  const d = parseLocalDate(event.date)
  const end = event.endDate ? parseLocalDate(event.endDate) : null

  return (
    <div className="flex gap-4">
      {/* Date chip */}
      <div className="shrink-0 w-14 text-center rounded-md border border-border overflow-hidden">
        <div className="bg-primary text-white text-[10px] font-semibold uppercase py-0.5">
          {MONTHS[d.getMonth()]}
        </div>
        <div className="text-xl font-heading font-bold text-text leading-tight py-1">{d.getDate()}</div>
      </div>

      {/* Details */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded ${meta.chip}`}>
            {meta.label}
          </span>
          <h3 className="font-medium text-text">{event.title}</h3>
        </div>
        <p className="text-sm text-text-muted mt-0.5">
          {end && end.getTime() !== d.getTime() && (
            <span>
              Through {MONTHS[end.getMonth()]} {end.getDate()}
              {event.time || event.location ? ' · ' : ''}
            </span>
          )}
          {event.time && <span>{event.time}</span>}
          {event.time && event.location ? ' · ' : ''}
          {event.location && <span>{event.location}</span>}
        </p>
        {event.description && (
          <p className="text-sm text-text mt-1 whitespace-pre-line">{event.description}</p>
        )}
      </div>
    </div>
  )
}
